import {
  BANKING_SOLUTIONS_ROUTE,
  HEALTHCARE_SOLUTIONS_ROUTE,
  INSURANCE_SOLUTIONS_ROUTE,
  PAYMENTS_SOLUTIONS_ROUTE,
  REAL_ESTATE_SOLUTIONS_ROUTE,
  RETAIL_SOLUTIONS_ROUTE,
  TELECOMMUNICATIONS_SOLUTIONS_ROUTE,
} from 'components/constants';

export type IndustryProduct = {
  title: string;
  text: string;
  signal: string;
};

export type IndustryOutcome = {
  title: string;
  text: string;
};

export type IndustryRole = {
  team: string;
  responsibility: string;
};

export type IndustryJourneyStep = {
  label: string;
  title: string;
  text: string;
};

export type IndustrySolution = {
  slug: string;
  route: string;
  name: string;
  cardTitle: string;
  cardText: string;
  cardProducts: [string, string, string];
  cardCta?: string;
  accent: string;
  softAccent: string;
  seo: {
    title: string;
    description: string;
  };
  hero: {
    eyebrow: string;
    title: string;
    text: string;
    previewLabel: string;
    previewQuestion: string;
    previewOptions: [string, string, string];
    previewResult: string;
  };
  outcomesTitle: string;
  outcomes: [IndustryOutcome, IndustryOutcome, IndustryOutcome];
  productsTitle: string;
  productsIntro: string;
  products: [IndustryProduct, IndustryProduct, IndustryProduct, IndustryProduct, IndustryProduct, IndustryProduct];
  journey: {
    eyebrow: string;
    title: string;
    text: string;
    steps: [IndustryJourneyStep, IndustryJourneyStep, IndustryJourneyStep, IndustryJourneyStep];
  };
  operatingTitle: string;
  operatingText: string;
  roles: [IndustryRole, IndustryRole, IndustryRole, IndustryRole, IndustryRole];
  related: [string, string];
  cta?: {
    title: string;
    text: string;
  };
};

export const industrySolutions: IndustrySolution[] = [
  {
    slug: 'insurance',
    route: INSURANCE_SOLUTIONS_ROUTE,
    name: 'Insurance',
    cardTitle: 'Insurance',
    cardText: 'Turn complex cover, claims and renewal decisions into clear customer journeys.',
    cardProducts: ['Coverage finder', 'Quote readiness', 'Claims preparation'],
    accent: '#0b7c66',
    softAccent: '#dff3ec',
    seo: {
      title: 'Interactive Customer Journeys for Insurance | Storycards',
      description:
        'Build insurance coverage finders, quote journeys, renewal reviews and claims preparation experiences with governed design and structured first-party data.',
    },
    hero: {
      eyebrow: 'Storycards for insurance',
      title: 'Make insurance easier to understand.',
      text:
        'Help customers understand cover, prepare for a quote and find the right service channel. Product teams maintain the questions and explanations. Marketing and service teams build from those shared starting points.',
      previewLabel: 'Coverage finder',
      previewQuestion: 'What would you like to protect?',
      previewOptions: ['My home', 'My car', 'My family'],
      previewResult: 'A focused path with the right questions and next action',
    },
    outcomesTitle: 'Clarity for customers. Control for the organization.',
    outcomes: [
      {
        title: 'Explain before the form',
        text: 'Help customers understand relevant cover and prepare the information they need before a quote or advisor conversation.',
      },
      {
        title: 'Collect better context',
        text: 'Capture structured responses and journey events that can flow to approved analytics, CRM or internal systems.',
      },
      {
        title: 'Reuse approved systems',
        text: 'Let teams create product and campaign versions from protected templates without rebuilding design and logic each time.',
      },
    ],
    productsTitle: 'Insurance products your teams can build.',
    productsIntro:
      'Each product starts with a real customer question and ends with a useful handoff. The experience can live on your site, inside a campaign or alongside an existing service flow.',
    products: [
      {
        title: 'Coverage finder',
        text: 'Ask about the person, asset and priorities, then present relevant product paths in clear language.',
        signal: 'Needs and preferences',
      },
      {
        title: 'Quote readiness journey',
        text: 'Help customers prepare the information an advisor needs, then direct them to the insurer’s quote service.',
        signal: 'Qualified context',
      },
      {
        title: 'Renewal review',
        text: 'Guide customers through material changes and questions before renewal or an agent conversation.',
        signal: 'Renewal intent',
      },
      {
        title: 'Claims preparation',
        text: 'Provide an approved checklist for incident details, documents and the correct service channel.',
        signal: 'Service readiness',
      },
      {
        title: 'Risk education assessment',
        text: 'Turn policy topics into scenario-based questions with practical, reviewed explanations.',
        signal: 'Education progress',
      },
      {
        title: 'Agent launch story',
        text: 'Give agents an interactive way to learn product changes, audiences and key talking points.',
        signal: 'Training completion',
      },
    ],
    journey: {
      eyebrow: 'Example customer journey',
      title: 'From a broad need to a useful next step.',
      text:
        'A coverage finder can adapt as the customer answers. It explains why each question matters, keeps the approved language intact and hands the result to the configured quote or service path.',
      steps: [
        { label: '01', title: 'Understand the need', text: 'Ask a short approved question about what the customer wants to protect.' },
        { label: '02', title: 'Adapt the path', text: 'Use conditional logic to show only the questions relevant to the selected need.' },
        { label: '03', title: 'Explain the result', text: 'Present a clear product path and the information used to shape it.' },
        { label: '04', title: 'Continue in your system', text: 'Send consented responses and events to the configured quote, CRM or analytics flow.' },
      ],
    },
    operatingTitle: 'One governed system across product, marketing and service.',
    operatingText:
      'Central owners define what cannot change. Authorized teams can still create, localize and improve customer journeys inside those rules.',
    roles: [
      { team: 'Design', responsibility: 'Own components, responsive behavior and approved interaction patterns.' },
      { team: 'Insurance product', responsibility: 'Maintain product content, questions and result logic.' },
      { team: 'Compliance', responsibility: 'Review language, required disclosures and publishing permissions.' },
      { team: 'Marketing and service', responsibility: 'Create governed journeys for acquisition, renewal and support.' },
      { team: 'Data', responsibility: 'Define consent, events and approved destinations for responses.' },
    ],
    related: ['banking', 'payments'],
  },
  {
    slug: 'banking',
    route: BANKING_SOLUTIONS_ROUTE,
    name: 'Banking',
    cardTitle: 'Banks',
    cardText: 'Help customers understand products and arrive at the next step with clearer intent.',
    cardProducts: ['Account finder', 'Mortgage readiness', 'Savings planner'],
    accent: '#3156a6',
    softAccent: '#e5ebf8',
    seo: {
      title: 'Interactive Banking Experiences and Product Finders | Storycards',
      description:
        'Create banking product finders, mortgage readiness journeys, savings planners and financial education experiences with enterprise governance.',
    },
    hero: {
      eyebrow: 'Storycards for banking',
      title: 'Turn complex banking products into guided decisions.',
      text:
        'Create interactive journeys that help customers compare options, prepare for an application and reach the right team with better context. Give every business unit room to create inside approved brand and compliance rules.',
      previewLabel: 'Account finder',
      previewQuestion: 'What matters most in your everyday account?',
      previewOptions: ['Digital control', 'Family banking', 'International use'],
      previewResult: 'A relevant product path with a clear reason to continue',
    },
    outcomesTitle: 'Better product understanding before the application starts.',
    outcomes: [
      {
        title: 'Guide product discovery',
        text: 'Replace long comparison tables with questions that explain the differences customers actually care about.',
      },
      {
        title: 'Prepare qualified conversations',
        text: 'Collect goals and preferences before a branch, advisor or digital application handoff.',
      },
      {
        title: 'Keep every journey governed',
        text: 'Protect approved language, components and publishing while product teams maintain current content.',
      },
    ],
    productsTitle: 'Banking journeys built around real customer intent.',
    productsIntro:
      'Use Storycards before an application, inside education campaigns or as a guided layer across product pages and digital banking services.',
    products: [
      { title: 'Account finder', text: 'Match everyday needs and service preferences to relevant account paths.', signal: 'Product preference' },
      { title: 'Mortgage readiness', text: 'Help prospective borrowers prepare priorities, documents and advisor questions.', signal: 'Readiness and intent' },
      { title: 'Savings goal planner', text: 'Turn a goal and time horizon into an educational plan with practical next actions.', signal: 'Goal context' },
      { title: 'Financial wellbeing check', text: 'Use reviewed questions and content to help customers understand habits and support options.', signal: 'Education progress' },
      { title: 'Business needs assessment', text: 'Route small businesses by payment, cash flow and service needs before a conversation.', signal: 'Business profile' },
      { title: 'Product education story', text: 'Explain loans, deposits or digital services through a short interactive narrative.', signal: 'Content engagement' },
    ],
    journey: {
      eyebrow: 'Example customer journey',
      title: 'Product discovery that feels like guidance, not a catalog.',
      text:
        'An account finder can ask about behavior and priorities, explain the relevance of each option and continue into the bank workflow selected by the organization.',
      steps: [
        { label: '01', title: 'Start with a goal', text: 'Ask what the customer is trying to do instead of asking them to choose a product name.' },
        { label: '02', title: 'Learn the context', text: 'Show relevant questions about usage, channels and preferences.' },
        { label: '03', title: 'Present a product path', text: 'Explain the relevant option and the reasons it fits the stated needs.' },
        { label: '04', title: 'Hand off with context', text: 'Continue to the approved application or advisor flow with consented journey data.' },
      ],
    },
    operatingTitle: 'A shared system for central standards and local execution.',
    operatingText:
      'Design, product and compliance teams define the system once. Growth, service and regional teams create approved versions without losing control.',
    roles: [
      { team: 'Design', responsibility: 'Create the responsive component system and reusable journey patterns.' },
      { team: 'Banking product', responsibility: 'Own current product facts, questions and handoff rules.' },
      { team: 'Compliance', responsibility: 'Approve language, disclosures, access and publishing workflows.' },
      { team: 'Growth and service', responsibility: 'Adapt approved journeys for products, audiences and channels.' },
      { team: 'Data', responsibility: 'Connect consented events and responses to the approved stack.' },
    ],
    related: ['payments', 'insurance'],
  },
  {
    slug: 'payments',
    route: PAYMENTS_SOLUTIONS_ROUTE,
    name: 'Payments and cards',
    cardTitle: 'Card issuers & payments',
    cardText: 'Make card choice, benefits, onboarding and merchant solutions easier to act on.',
    cardProducts: ['Card matcher', 'Benefits explorer', 'Merchant finder'],
    accent: '#6c3bb8',
    softAccent: '#eee5f8',
    seo: {
      title: 'Card and Payment Customer Engagement | Storycards',
      description:
        'Build credit card matchers, benefits explorers, onboarding journeys and merchant solution finders with Storycards enterprise controls.',
    },
    hero: {
      eyebrow: 'Storycards for payments and card issuers',
      title: 'Make card benefits easier to find and use.',
      text:
        'Build guided experiences for acquisition, onboarding, benefits and merchant solutions. Keep changing offers clear, on brand and connected to the systems your teams already use.',
      previewLabel: 'Card matcher',
      previewQuestion: 'Where should your card work hardest?',
      previewOptions: ['Travel', 'Everyday rewards', 'Business spending'],
      previewResult: 'A card path shaped by spending priorities and benefit preferences',
    },
    outcomesTitle: 'Move from feature lists to relevant product stories.',
    outcomes: [
      { title: 'Make comparison useful', text: 'Ask about real spending behavior, then explain benefits in the context customers understand.' },
      { title: 'Support the full lifecycle', text: 'Reuse the same governed system across acquisition, activation, benefits and retention.' },
      { title: 'Keep offers current', text: 'Let product owners update approved content while brand and compliance controls stay in place.' },
    ],
    productsTitle: 'Products for cardholders, merchants and growth teams.',
    productsIntro:
      'Create focused journeys that explain value, capture preference and continue into your application, wallet, merchant or service experience.',
    products: [
      { title: 'Card matcher', text: 'Guide customers by spending pattern, benefit preference and service needs.', signal: 'Card preference' },
      { title: 'Benefits explorer', text: 'Make rewards, travel, protection and partner benefits easy to find and understand.', signal: 'Benefit interest' },
      { title: 'New card onboarding', text: 'Guide activation, wallet setup, controls and first-use tasks in a clear sequence.', signal: 'Onboarding progress' },
      { title: 'Merchant solution finder', text: 'Route businesses by size, sales channel, settlement and payment needs.', signal: 'Merchant profile' },
      { title: 'Rewards planner', text: 'Help cardholders understand earning and redemption paths with approved examples.', signal: 'Reward preference' },
      { title: 'Fraud awareness scenarios', text: 'Teach safer behavior with short, scenario-based questions and reviewed guidance.', signal: 'Education completion' },
    ],
    journey: {
      eyebrow: 'Example customer journey',
      title: 'Show the benefits that matter to this customer.',
      text:
        'A card matcher can begin with how the customer spends, narrow the relevant benefit set and explain why a specific product path may be worth exploring.',
      steps: [
        { label: '01', title: 'Choose a priority', text: 'Start with travel, rewards, business use or another approved customer need.' },
        { label: '02', title: 'Refine the pattern', text: 'Ask only the follow-up questions relevant to that priority.' },
        { label: '03', title: 'Explain the value', text: 'Show a focused product path and the benefits connected to the stated preferences.' },
        { label: '04', title: 'Continue securely', text: 'Move to the issuer application or service flow and pass only configured, consented data.' },
      ],
    },
    operatingTitle: 'A reusable engagement layer for changing products and offers.',
    operatingText:
      'Product owners maintain accuracy while design, compliance, campaign and data teams keep each experience consistent with organizational rules.',
    roles: [
      { team: 'Design', responsibility: 'Own card, offer and journey components across screen sizes.' },
      { team: 'Product', responsibility: 'Maintain current benefits, questions and destination rules.' },
      { team: 'Compliance', responsibility: 'Control approved language, disclosures and publishing rights.' },
      { team: 'Campaign teams', responsibility: 'Create channel and audience versions from protected templates.' },
      { team: 'Data', responsibility: 'Define tracking, consent and approved destinations for interaction events.' },
    ],
    related: ['banking', 'retail'],
  },
  {
    slug: 'healthcare',
    route: HEALTHCARE_SOLUTIONS_ROUTE,
    name: 'Healthcare',
    cardTitle: 'Healthcare organizations',
    cardText: 'Guide members and patients to services, preparation and approved information.',
    cardProducts: ['Service navigator', 'Benefit explainer', 'Patient preparation'],
    accent: '#087f8c',
    softAccent: '#dff2f3',
    seo: {
      title: 'Interactive Healthcare and Patient Engagement | Storycards',
      description:
        'Create healthcare service navigators, member onboarding, benefit explainers and patient education journeys with controlled content and data flows.',
    },
    hero: {
      eyebrow: 'Storycards for healthcare',
      title: 'Guide people through care and services with more clarity.',
      text:
        'Help health funds, health plans and care providers guide people to services, appointment preparation and benefit information. Clinical and privacy owners review the content while communications teams maintain each journey.',
      previewLabel: 'Service navigator',
      previewQuestion: 'What kind of support are you looking for?',
      previewOptions: ['Find a service', 'Prepare for a visit', 'Understand my plan'],
      previewResult: 'A relevant service path with approved preparation information',
    },
    outcomesTitle: 'Useful guidance without turning content into diagnosis.',
    outcomes: [
      { title: 'Reduce navigation friction', text: 'Ask simple service questions and route people to the right information, location or support channel.' },
      { title: 'Keep content reviewed', text: 'Protect clinical, legal and privacy approved language inside reusable journey systems.' },
      { title: 'Understand the experience', text: 'Measure completion, drop-off and service intent through configured first-party events.' },
    ],
    productsTitle: 'Healthcare journeys for education, access and service.',
    productsIntro:
      'Storycards can support navigation and communication before and after a service interaction. It does not diagnose or replace clinical decision systems.',
    products: [
      { title: 'Service navigator', text: 'Route people by need, location, channel and approved service criteria.', signal: 'Service intent' },
      { title: 'Plan and benefit explainer', text: 'Break common coverage topics into clear questions and reviewed explanations.', signal: 'Benefit interest' },
      { title: 'Appointment preparation', text: 'Present approved instructions, documents and questions before a visit.', signal: 'Preparation progress' },
      { title: 'Preventive care education', text: 'Explain reviewed care topics and link to the organization’s reminder or booking service.', signal: 'Education completion' },
      { title: 'Member onboarding', text: 'Introduce digital services, support channels and plan resources in sequence.', signal: 'Onboarding progress' },
      { title: 'Patient feedback journey', text: 'Collect structured service feedback with follow-up questions based on responses.', signal: 'Experience feedback' },
    ],
    journey: {
      eyebrow: 'Example service journey',
      title: 'Move from a broad request to the right service path.',
      text:
        'A service navigator can ask non-clinical questions, adapt by location and preference, then connect the person with approved information or the correct organizational channel.',
      steps: [
        { label: '01', title: 'Identify the request', text: 'Ask whether the person needs navigation, preparation, plan information or support.' },
        { label: '02', title: 'Show relevant questions', text: 'Adapt by location, channel and other approved service criteria.' },
        { label: '03', title: 'Present the service path', text: 'Explain the next step with reviewed content and clear boundaries.' },
        { label: '04', title: 'Connect to care or support', text: 'Continue to the configured booking, directory or support destination.' },
      ],
    },
    operatingTitle: 'Communication teams can create while health content stays controlled.',
    operatingText:
      'Clinical, legal and privacy owners approve the system. Authorized service and communications teams use it to publish clear, consistent journeys.',
    roles: [
      { team: 'Design', responsibility: 'Build accessible components and responsive service patterns.' },
      { team: 'Clinical and service owners', responsibility: 'Maintain approved instructions, routes and content boundaries.' },
      { team: 'Legal and privacy', responsibility: 'Approve language, data fields, access and publishing rules.' },
      { team: 'Communications', responsibility: 'Create member and patient journeys from protected templates.' },
      { team: 'Data', responsibility: 'Define consent, retention and approved event destinations.' },
    ],
    related: ['insurance', 'banking'],
  },
  {
    slug: 'real-estate',
    route: REAL_ESTATE_SOLUTIONS_ROUTE,
    name: 'Real estate',
    cardTitle: 'Real estate companies',
    cardText: 'Match buyers, renters and investors with the right property and sales path.',
    cardProducts: ['Property matcher', 'Buyer qualification', 'Project story'],
    accent: '#a55a24',
    softAccent: '#f6eadf',
    seo: {
      title: 'Interactive Real Estate Marketing and Lead Journeys | Storycards',
      description:
        'Build property matchers, buyer qualification flows, project stories and seller intake journeys with reusable design and CRM-ready data.',
    },
    hero: {
      eyebrow: 'Storycards for real estate',
      title: 'Turn property interest into a better sales conversation.',
      text:
        'Build interactive journeys that help buyers, renters, sellers and investors clarify what they need. Reuse one approved system across projects, markets and campaigns while routing structured context to the right team.',
      previewLabel: 'Property matcher',
      previewQuestion: 'What should your next property make possible?',
      previewOptions: ['A family home', 'A city investment', 'A flexible workspace'],
      previewResult: 'A focused property path with priorities ready for the sales team',
    },
    outcomesTitle: 'Qualify the intent before the lead reaches sales.',
    outcomes: [
      { title: 'Make discovery personal', text: 'Guide people by budget, location, use and priorities instead of asking them to search an undifferentiated list.' },
      { title: 'Give sales useful context', text: 'Capture structured preferences and route each inquiry to the relevant project or team.' },
      { title: 'Launch from one system', text: 'Reuse approved layouts, logic and tracking across properties without recreating the experience.' },
    ],
    productsTitle: 'Interactive products for the complete property journey.',
    productsIntro:
      'Use focused experiences for discovery, project marketing, qualification and seller intake. Each one can reflect the project brand while staying inside the company system.',
    products: [
      { title: 'Property matcher', text: 'Guide prospects by location, budget, property type and lifestyle priorities.', signal: 'Property preference' },
      { title: 'Buyer qualification', text: 'Collect timing, financing status and viewing preferences before sales follows up.', signal: 'Lead readiness' },
      { title: 'Project and neighborhood story', text: 'Explore plans, amenities and surroundings through an interactive narrative.', signal: 'Content engagement' },
      { title: 'Affordability preparation', text: 'Help buyers organize budget inputs and the questions to discuss with an advisor.', signal: 'Preparation intent' },
      { title: 'Seller readiness assessment', text: 'Collect property context, timeline and service needs before valuation intake.', signal: 'Seller profile' },
      { title: 'Investor opportunity explainer', text: 'Present the project case and capture structured investor interest for follow-up.', signal: 'Investor interest' },
    ],
    journey: {
      eyebrow: 'Example buyer journey',
      title: 'Start with the life the buyer wants, then find the property.',
      text:
        'A property matcher can combine practical filters with lifestyle priorities, explain the fit and route the buyer to the team responsible for the relevant project.',
      steps: [
        { label: '01', title: 'Define the goal', text: 'Ask whether the person is buying, renting, investing or planning a future move.' },
        { label: '02', title: 'Learn the priorities', text: 'Adapt questions around location, timing, budget and daily needs.' },
        { label: '03', title: 'Present relevant options', text: 'Show a focused property or project path and explain the match.' },
        { label: '04', title: 'Route the inquiry', text: 'Send consented preferences to the configured CRM flow and responsible team.' },
      ],
    },
    operatingTitle: 'One launch system for every property, market and campaign.',
    operatingText:
      'Central teams own the experience system. Project and sales teams adapt approved content without rebuilding the design, logic or data layer.',
    roles: [
      { team: 'Design and brand', responsibility: 'Create reusable property, project and campaign components.' },
      { team: 'Project marketing', responsibility: 'Maintain current availability, content and audience paths.' },
      { team: 'Legal', responsibility: 'Review claims, disclosures and publishing requirements.' },
      { team: 'Sales', responsibility: 'Define qualification questions and lead routing rules.' },
      { team: 'Data and CRM', responsibility: 'Connect consented preferences and events to approved workflows.' },
    ],
    related: ['retail', 'banking'],
  },
  {
    slug: 'retail',
    route: RETAIL_SOLUTIONS_ROUTE,
    name: 'Retail and ecommerce',
    cardTitle: 'Retail & ecommerce',
    cardText: 'Guide shoppers to relevant products and collect preference data directly.',
    cardProducts: ['Product finder', 'Guided selling', 'Routine builder'],
    accent: '#bd3f68',
    softAccent: '#f8e4eb',
    seo: {
      title: 'Ecommerce Product Finders and Guided Selling | Storycards',
      description:
        'Create product finders, guided selling journeys, gift finders and routine builders that connect preference data to your ecommerce stack.',
    },
    hero: {
      eyebrow: 'Storycards for retail and ecommerce',
      title: 'Help every shopper find the product that fits.',
      text:
        'Create guided selling experiences that turn product choice into a useful conversation. Use preference data to shape the journey, explain recommendations and continue into your existing ecommerce experience.',
      previewLabel: 'Product finder',
      previewQuestion: 'What matters most in the product you choose?',
      previewOptions: ['Performance', 'Everyday simplicity', 'A thoughtful gift'],
      previewResult: 'A relevant product set with reasons, content and a direct next action',
    },
    outcomesTitle: 'Better discovery for shoppers. Better signals for the brand.',
    outcomes: [
      { title: 'Reduce choice overload', text: 'Ask about needs and context, then show a focused set of relevant products.' },
      { title: 'Collect declared preference', text: 'Learn directly from the choices shoppers make instead of relying only on passive behavior.' },
      { title: 'Reuse across campaigns', text: 'Adapt approved finders and stories for launches, seasons and audiences without rebuilding them.' },
    ],
    productsTitle: 'Shopping journeys for discovery, education and repeat use.',
    productsIntro:
      'Build a focused layer before product listing, inside a campaign or after purchase. Keep the experience connected to current product data and the brand system.',
    products: [
      { title: 'Product finder', text: 'Guide shoppers by need, preference, use and purchase context.', signal: 'Declared preference' },
      { title: 'Guided selling journey', text: 'Explain meaningful differences for considered or technical products.', signal: 'Decision context' },
      { title: 'Gift finder', text: 'Match recipient, occasion, interest and budget to relevant products.', signal: 'Gift intent' },
      { title: 'Routine or bundle builder', text: 'Combine compatible products around a goal, routine or environment.', signal: 'Bundle interest' },
      { title: 'Interactive launch story', text: 'Introduce a new product through questions, demonstrations and relevant details.', signal: 'Launch engagement' },
      { title: 'Post-purchase onboarding', text: 'Guide setup, care, feature discovery and repeat-use habits after checkout.', signal: 'Onboarding progress' },
    ],
    journey: {
      eyebrow: 'Example shopping journey',
      title: 'Turn a product catalog into a guided conversation.',
      text:
        'A product finder can begin with the shopper need, adapt around context and explain a focused recommendation before continuing to the product or cart experience.',
      steps: [
        { label: '01', title: 'Start with the use', text: 'Ask what the shopper wants to achieve instead of asking them to choose a category.' },
        { label: '02', title: 'Learn the preference', text: 'Show follow-up questions about context, priorities and constraints.' },
        { label: '03', title: 'Explain the match', text: 'Present relevant products with the reasons each one fits the selected needs.' },
        { label: '04', title: 'Continue to commerce', text: 'Send the shopper to the current product or cart path and record configured events.' },
      ],
    },
    operatingTitle: 'A guided selling system that campaign teams can actually reuse.',
    operatingText:
      'Brand, ecommerce and product teams define the system. Campaign teams create seasonal and audience versions while logic, data and presentation stay consistent.',
    roles: [
      { team: 'Design and brand', responsibility: 'Own reusable product, campaign and interaction components.' },
      { team: 'Ecommerce', responsibility: 'Connect current products, destinations and merchandising rules.' },
      { team: 'Product content', responsibility: 'Maintain accurate product facts and recommendation explanations.' },
      { team: 'Campaign teams', responsibility: 'Create launch, seasonal and audience versions from approved templates.' },
      { team: 'Data', responsibility: 'Define preference events and approved analytics or CRM destinations.' },
    ],
    related: ['payments', 'real-estate'],
  },
  {
    slug: 'telecommunications',
    route: TELECOMMUNICATIONS_SOLUTIONS_ROUTE,
    name: 'Telecommunications',
    cardTitle: 'Telecom companies',
    cardText: 'Guide customers through plans, devices, onboarding and service with clearer next steps.',
    cardProducts: ['Plan finder', 'Device matcher', 'Roaming planner'],
    accent: '#087f8c',
    softAccent: '#dff3f4',
    seo: {
      title: 'Interactive Telecom Customer Journeys | Storycards',
      description:
        'Build telecom plan finders, device matchers, onboarding journeys and service navigators with governed design and structured customer data.',
    },
    hero: {
      eyebrow: 'Storycards for telecommunications',
      title: 'Make complex telecom choices easier to act on.',
      text:
        'Help customers compare plans, choose devices, prepare for travel and reach the right service path. Keep current product content, brand rules, permissions and data handoffs inside one governed system.',
      previewLabel: 'Plan finder',
      previewQuestion: 'What matters most in your mobile plan?',
      previewOptions: ['Reliable everyday use', 'More data and streaming', 'International travel'],
      previewResult: 'A focused plan path with the relevant benefits and next action',
    },
    outcomesTitle: 'Clearer choices for customers. Reusable journeys for every channel.',
    outcomes: [
      {
        title: 'Explain plans in context',
        text: 'Start with how the customer uses the service, then explain the relevant plan, device or add-on choices.',
      },
      {
        title: 'Prepare better handoffs',
        text: 'Collect structured needs and intent before the customer continues to ecommerce, sales or service.',
      },
      {
        title: 'Keep offers consistent',
        text: 'Reuse approved product journeys across campaigns, retail teams and digital channels without rebuilding the system.',
      },
    ],
    productsTitle: 'Interactive products across acquisition, onboarding and service.',
    productsIntro:
      'Use focused journeys to make changing plans, devices and service options understandable. Each product can connect to the destination and data flow the organization approves.',
    products: [
      {
        title: 'Plan finder',
        text: 'Guide customers by household, usage, content, travel and budget instead of asking them to compare a long plan table.',
        signal: 'Plan preference',
      },
      {
        title: 'Device matcher',
        text: 'Match practical needs, operating preferences and price range to a focused set of devices.',
        signal: 'Device intent',
      },
      {
        title: 'Roaming planner',
        text: 'Explain travel options based on destination, trip length and expected calls, messages and data use.',
        signal: 'Travel context',
      },
      {
        title: 'New customer onboarding',
        text: 'Guide activation, account setup, app installation and the first useful service actions.',
        signal: 'Setup progress',
      },
      {
        title: 'Service navigator',
        text: 'Ask a short set of questions and route customers to the relevant self-service, support or retail path.',
        signal: 'Service need',
      },
      {
        title: 'Business needs assessment',
        text: 'Collect team size, locations, connectivity needs and timing before a business sales conversation.',
        signal: 'Business context',
      },
    ],
    journey: {
      eyebrow: 'Example customer journey',
      title: 'Start with how the customer connects, then narrow the choice.',
      text:
        'A plan finder can adapt around usage, household and travel needs, explain why an option fits and continue into the organization’s approved sales path.',
      steps: [
        { label: '01', title: 'Understand the use', text: 'Ask about devices, data, household and the services the customer relies on.' },
        { label: '02', title: 'Adapt the questions', text: 'Show only the follow-up questions relevant to the selected needs and context.' },
        { label: '03', title: 'Explain the plan path', text: 'Present a focused option with the benefits and assumptions that shaped the result.' },
        { label: '04', title: 'Continue in the approved channel', text: 'Send consented responses and events to the configured ecommerce, CRM or analytics flow.' },
      ],
    },
    operatingTitle: 'One product system across digital, retail and service teams.',
    operatingText:
      'Central owners maintain current products and rules. Channel teams create approved journeys for launches, campaigns, stores and service without duplicating the underlying logic.',
    roles: [
      { team: 'Design and brand', responsibility: 'Own reusable components, responsive behavior and presentation rules.' },
      { team: 'Product', responsibility: 'Maintain current plans, devices, benefits and journey logic.' },
      { team: 'Digital commerce', responsibility: 'Connect recommendations and handoffs to approved purchase paths.' },
      { team: 'Retail and service', responsibility: 'Create governed journeys for onboarding, education and support.' },
      { team: 'Data and CRM', responsibility: 'Define consent, events and approved destinations for customer responses.' },
    ],
    related: ['payments', 'retail'],
  },
];

export const industrySolutionBySlug = industrySolutions.reduce<Record<string, IndustrySolution>>((result, industry) => {
  result[industry.slug] = industry;
  return result;
}, Object.create(null));
